import {UseMutationResult, useQueryClient} from "@tanstack/react-query";
import {Spin, Divider, Space, Alert, Button} from "antd";
import {AxiosError} from "axios";
import moment from "moment";
import {useState} from "react";
import {useBookingData} from "../../../../../../../container/booking/booking-context";
import {
  TCustomerPaidBundleApiResponse,
  TCustomerPaidBundleServiceApiResponse,
} from "../../../../../../../react-query-hooks/customer";
import {
  bundleBookingErrorMessage,
  TBundleSessionBookingPayload,
} from "../../../../../../../react-query-hooks/public-profile";
import {
  StyledBookingRightColumnHeader,
  StyledBookingRightColumnWrapper,
  StyledBookingTimeComponent,
  StyledBookingTimeComponentItemWrapper,
} from "../../../../../../../styles/booking/booking";
import {BundleCalendar} from "../../../../../../calendar/customer/bundle-calendar";
import {RootState} from "../../../../../../../redux/store";
import {useSelector} from "react-redux";
import {
  availableTimes,
  useBookingSchedule,
  UseBookingTimeSlotsResult,
} from "../../../../../../../features/booking-availability";

type TCalendarContentProps = {
  updateStep: (step: number) => void;
  currentStep?: number;
  timeSlots: UseBookingTimeSlotsResult;
  bundleBookingMutation: UseMutationResult<
    any,
    AxiosError<
      {
        message: string;
      },
      any
    >,
    TBundleSessionBookingPayload,
    unknown
  >;
  /** The offering as carried on the bundle row; no separate fetch. */
  service: TCustomerPaidBundleServiceApiResponse;
  /** The coach's user id, resolved from their username by the modal. */
  mentorId: number;
  bundle: TCustomerPaidBundleApiResponse;
};

const CalendarContent: React.FC<TCalendarContentProps> = ({
  updateStep,
  timeSlots,
  bundleBookingMutation,
  service,
  mentorId,
  bundle,
}) => {
  const {userInfo} = useSelector((state: RootState) => state.userLogin);
  const {setValues, data} = useBookingData();
  const [showConfirm, setShowConfirm] = useState("");
  const queryClient = useQueryClient();

  const serviceId = service.id;

  // The weekdays the coach takes bookings on and the dates they have blocked
  // out, each its own v2 endpoint.
  const schedule = useBookingSchedule({mentorId, serviceId, fallback: {}});

  const handleConfirm = () => {
    setValues({...data, timeSelected: showConfirm});
    const tz = moment.tz.guess(true);
    const payload: TBundleSessionBookingPayload = {
      // The session is drawn from this exact bundle, so a customer holding two
      // bundles for the same service no longer always draws from the newest.
      bundleReference: bundle.reference,
      serviceId,
      // The customer's own details - v2 persists these onto the booking row as
      // the attendee. (This used to send the coach's name by mistake.)
      firstName: userInfo?.customer?.firstName ?? userInfo?.user?.firstName ?? "",
      lastName: userInfo?.customer?.lastName ?? userInfo?.user?.lastName ?? "",
      // No emailAddress: the route reads the customer off the bearer token, so
      // there is nothing left to resolve on a customer-only login.
      userTimezone: data.selectedTimeZone ?? tz,
      bookingDate: data.booking_date,
      bookingYear: data.booking_year,
      bookingMonth: data.booking_month,
      timeSlot: showConfirm,
    };
    bundleBookingMutation.mutate(payload, {
      onSuccess: () => {
        updateStep(2);
      },
      onSettled: () =>
        Promise.all([
          queryClient.invalidateQueries({queryKey: ["customer_upcoming_calls"]}),
          queryClient.invalidateQueries({queryKey: ["customer_paid_bundles"]}),
        ]),
    });
  };

  return (
    <div>
      <StyledBookingRightColumnHeader>
        Select date and time for your next session
      </StyledBookingRightColumnHeader>
      <div>
        {bundleBookingMutation.isError && (
          <Alert
            message="Error"
            // 404 (not yours / gone) and 409 (inactive, no sessions left, or the
            // booking interval has not elapsed) all arrive with a message
            // written for the customer, so show it as-is.
            description={bundleBookingErrorMessage(bundleBookingMutation.error)}
            type="error"
            showIcon
            closable
          />
        )}
      </div>
      <StyledBookingRightColumnWrapper>
        <div>
          {/* The calendar grid is built once on mount, so it only renders after
              the schedule has resolved. */}
          {schedule.isLoading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "30vh",
                width: "100%",
              }}
            >
              <Spin size="large" />
            </div>
          ) : schedule.isError ? (
            <Alert
              message="Error"
              description={
                schedule.error?.message ?? "We couldn't load this coach's availability."
              }
              type="error"
              showIcon
            />
          ) : (
            <BundleCalendar
              timeSlots={timeSlots}
              mentorId={mentorId}
              serviceId={serviceId}
              availableWeekdays={schedule.availableWeekdays}
              unavailableDates={schedule.unavailableDates}
            />
          )}
        </div>
        {timeSlots.isLoading && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "65vh",
              width: "100%",
            }}
          >
            <Spin size="large" />
          </div>
        )}
        {timeSlots.isSuccess && timeSlots.data && (
          <StyledBookingTimeComponent id="time-component">
            <div className="time-component-heading">
              {data.selectedDateString.toDateString()}
            </div>
            <Divider style={{marginTop: "1.8rem", marginBottom: "2.4rem"}} />
            <Space size={8} direction="vertical" style={{display: "flex"}}>
              {availableTimes(timeSlots.data.slots).map((timeSlot: string) => (
                <StyledBookingTimeComponentItemWrapper key={timeSlot}>
                  <Button block size="small" onClick={() => setShowConfirm(timeSlot)}>
                    {timeSlot}
                  </Button>
                  {showConfirm === timeSlot && (
                    <Button
                      type="primary"
                      block
                      size="small"
                      onClick={handleConfirm}
                      loading={bundleBookingMutation.isLoading}
                    >
                      Confirm
                    </Button>
                  )}
                </StyledBookingTimeComponentItemWrapper>
              ))}
            </Space>
          </StyledBookingTimeComponent>
        )}
      </StyledBookingRightColumnWrapper>
    </div>
  );
};

export {CalendarContent};
